import { ApiPagination, ICallback } from '../api-types';

export interface NotificationItem {
  title: string;
  imageUrl?: string;
  date: string;
  type: NotificationTypes;
  userName?: string;
  eventName?: string;
  action?: string;
  read: boolean;
}

export type NotificationTypes =
  | 'WORKSHOP_JOINED'
  | 'WEBINAR_JOINED'
  | 'CHALLENGE_JOINED'
  | 'EARNED_A_BADGE'
  | 'BULLPEN_APPLICATIONS_OPEN';

export interface NotificationGroup {
  date: string;
  notifications: NotificationItem[];
}

export enum NotificationType {
  //Upon Student Opening the module and navigating to the video page
  MODULE_STARTED = 'MODULE_STARTED',

  //Drop off point without completing the module
  MODULE_SAVED = 'MODULE_SAVED',

  //Upon reaching the point where they see their coins and points
  MODULE_COMPLETED = 'MODULE_COMPLETED',

  //Upon finishing an entire skill or Track Subsection
  BADGE_EARNED = 'BADGE_EARNED',

  //Upon finishing an entire pillar or full track
  CERTIFICATE_EARNED = 'CERTIFICATE_EARNED',

  //When student writes a journal entry in the module, activity or assessment section
  JOURNAL_ENTRY_SAVED = 'JOURNAL_ENTRY_SAVED',

  //Should be pre-set as soon as they login so that they know they should go complete it.
  FINISH_YOUR_BASELINE_ASSESSMENT = 'FINISH_YOUR_BASELINE_ASSESSMENT',

  //Upon completion of Baseline Assessment
  BASELINE_ASSESSMENT_COMPLETE = 'BASELINE_ASSESSMENT_COMPLETE',

  //If name, email or photo is changed - this is a confirmation message
  PROFILE_UPDATED = 'PROFILE_UPDATED',

  //Upon creating a report and downloading it
  REPORT_SUCCESSFULLY_DOWNLOADED = 'REPORT_SUCCESSFULLY_DOWNLOADED',

  //Upon adding a new project
  PROJECT_ADDED = 'PROJECT_ADDED',

  //When a resource is added to favorites
  RESOURCE_ADDED_TO_FAVORITE = 'RESOURCE_ADDED_TO_FAVORITE',

  //When a student pays coins to unlock a resource for download
  RESOURCE_UNLOCKED_FOR_DOWNLOAD = 'RESOURCE_UNLOCKED_FOR_DOWNLOAD',

  //Upon submission of application
  APPLICATION_SUBMITTED = 'APPLICATION_SUBMITTED',

  //Upon Start but not completed application (Saved)
  APPLICATION_SAVED = 'APPLICATION_SAVED',

  //When Admin Adds a New Bullpen Event
  NEW_BULLPEN_EVENT = 'NEW_BULLPEN_EVENT',

  //When student confirms they've applied to a job
  JOB_APPLIED = 'JOB_APPLIED',

  //When student favorites a job
  JOB_FAVORITED = 'JOB_FAVORITED',

  //When Admin Adds a new job
  NEW_JOB_ADDED = 'NEW_JOB_ADDED',

  //When student purchases entry to event
  EVENT_CONFIRMED = 'EVENT_CONFIRMED',

  //When Admin adds a new event
  NEW_EVENT = 'NEW_EVENT',

  //When student starts a challenge - Clicks on participate button
  CHALLENGE_STARTED = 'CHALLENGE_STARTED',

  //24 hours to deadline - if student hasn't submitted yet
  CHALLENGE_DEADLINE_APPROACHING = 'CHALLENGE_DEADLINE_APPROACHING',

  //If student submits before deadline
  CHALLENGE_COMPLETE = 'CHALLENGE_COMPLETE',

  //If student doesn't submit before time limit is up
  CHALLENGE_DEADLINE_PASSED = 'CHALLENGE_DEADLINE_PASSED',

  // If student receives a new feedback
  CHALLENGE_FEEDBACK_RECEIVED = 'CHALLENGE_FEEDBACK_RECEIVED',

  //When there's a new writing competition
  WRITING_COMPETITION_OPEN = 'WRITING_COMPETITION_OPEN',

  //When the student submits a writing competition essay successfully
  WRITING_COMPETITION_SUBMISSION_SUCCESSFUL = 'WRITING_COMPETITION_SUBMISSION_SUCCESSFUL',

  //When Admin selects winners for the writing competition
  WRITING_COMPETITION_RESULTS_OUT = 'WRITING_COMPETITION_RESULTS_OUT',

  //When student shares a certificate successfully
  CERTIFICATE_SHARED_SUCCESSFULLY = 'CERTIFICATE_SHARED_SUCCESSFULLY',

  //When student Submits a request
  PRIVATE_SESSION_REQUEST_SUBMITTED = 'PRIVATE_SESSION_REQUEST_SUBMITTED',

  //When Admin confirms time and link
  PRIVATE_SESSION_BOOKED_SUCCESSFULLY = 'PRIVATE_SESSION_BOOKED_SUCCESSFULLY',

  //When student purchases recording of the event
  EVENT_RECORDING_PURCHASED = 'EVENT_RECORDING_PURCHASED',

  //When student submits an inquiry
  INQUIRY_SUBMITTED = 'INQUIRY_SUBMITTED',

  //When student changes Password
  PASSWORD_CHANGED_SUCCESSFULLY = 'PASSWORD_CHANGED_SUCCESSFULLY',
}
export enum AdminNotificationType {
  UPLOADED_STUDENTS_CSV = 'UPLOADED_STUDENTS_CSV',
  STUDENT_ADDED = 'STUDENT_ADDED',
  STUDENT_REMOVED = 'STUDENT_REMOVED',
  STUDENT_PROFILE_EDITED = 'STUDENT_PROFILE_EDITED',
  BATCH_CREATED = 'BATCH_CREATED',
  STUDENT_ADDED_TO_BATCH = 'STUDENT_ADDED_TO_BATCH',
  STUDENT_REMOVED_FROM_BATCH = 'STUDENT_REMOVED_FROM_BATCH',
  CREDITS_PURCHASED = 'CREDITS_PURCHASED',
  INVOICE_DOWNLOADED = 'INVOICE_DOWNLOADED',
  TEAM_MEMBER_ADDED_TO_TEAM = 'TEAM_MEMBER_ADDED_TO_TEAM',
  TEAM_MEMBER_ROLE_CHANGED = 'TEAM_MEMBER_ROLE_CHANGED',
  ACTION_LOG_DOWNLOADED = 'ACTION_LOG_DOWNLOADED',
  TEAM_MEMBER_REMOVED = 'TEAM_MEMBER_REMOVED',
  NEW_NEWS_ITEMS = 'NEW_NEWS_ITEMS',
}

export interface INotificationListItem<
  T extends NotificationType | AdminNotificationType = NotificationType
> {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  body: {
    [key: string]: string;
  };
  studentId: string;
  read: boolean;
  type: T;
  seen?: boolean;
}
export interface INotificationList<
  T extends NotificationType | AdminNotificationType = NotificationType
> {
  notifications: INotificationListItem<T>[];
  newNotificationsCount: number;
  pagination: ApiPagination;
}

type TNotificationType = `${NotificationType}`;
export type CreateNotificationType = Extract<
  TNotificationType,
  | 'MODULE_STARTED'
  | 'MODULE_SAVED'
  | 'MODULE_COMPLETED'
  | 'REPORT_SUCCESSFULLY_DOWNLOADED'
  | 'BASELINE_ASSESSMENT_COMPLETE'
  | 'CERTIFICATE_SHARED_SUCCESSFULLY'
>;
export interface ICreateNotificationRequest {
  type: CreateNotificationType;
  body?: {
    [key: string]: string;
  };
}

export interface ICreateNotificationQuery {
  params: ICreateNotificationRequest;
  cb?: ICallback;
}
